import { Message } from "@arco-design/web-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";

import {
  crmCustomerWorkbenchDefaultListParams,
  crmCustomerWorkbenchQueryRootKey,
} from "../config";
import {
  confirmCustomerAssignment,
  createCustomerWorkbenchCustomer,
  deleteCustomerWorkbenchCustomer,
  disableCustomerWorkbenchCustomer,
  enableCustomerWorkbenchCustomer,
  fetchCustomerWorkbenchContacts,
  fetchCustomerWorkbenchDetail,
  fetchCustomerWorkbenchList,
  fetchCustomerWorkbenchMetadata,
  parseCustomerWorkbenchNlQuery,
  revokeCustomerSharedOwner,
  searchCustomerWorkbenchOrganizationUnits,
  searchCustomerWorkbenchOrganizations,
  updateCustomerWorkbenchCustomer,
} from "../repo/crmCustomerWorkbenchRepo";
import {
  formatCustomerWorkbenchError,
  normalizeCustomerWorkbenchFilters,
} from "../service";
import type {
  CustomerAssignmentConfirmPayload,
  CustomerWorkbenchFormPayload,
  CustomerWorkbenchListFilters,
  CustomerWorkbenchListParams,
  CustomerWorkbenchNlQueryParseResult,
  CustomerWorkbenchSelectionResolvePayload,
  CustomerWorkbenchViewScope,
} from "../types";

const CUSTOMER_WORKBENCH_ALL_SELECTION_PAGE_SIZE = 100;
const CUSTOMER_WORKBENCH_PAGE_SIZE_OPTIONS = new Set([10, 20, 50, 100]);
const CUSTOMER_WORKBENCH_SORT_FIELDS = new Set([
  "customer_name",
  "org_unit_name",
  "anchor_level",
  "primary_owner_name",
  "shared_owner_count",
  "opportunity_count",
  "province",
  "city",
  "org_name",
  "contact_count",
  "created_at",
  "updated_at",
]);
/** 旧默认排序；无 sortDefault 标记时把残留的 created_at 降序迁到联系人数降序。 */
const LEGACY_DEFAULT_SORT_FIELD = "created_at";
const CUSTOMER_WORKBENCH_SORT_DEFAULT_VERSION = "contact_count_v1";

type CustomerSharedOwnerReplacePayload = {
  customerId: number;
  currentUserIds: number[];
  targetUserIds: number[];
  currentUserId: number;
};

type CustomerWorkbenchUrlState = {
  filters: CustomerWorkbenchListFilters;
  viewScope: CustomerWorkbenchViewScope;
  page: number;
  pageSize: number;
  sortField: string;
  sortDirection: "asc" | "desc";
};

/** 编排客户工作台筛选、分页、排序、分配和联系人抽屉状态。 */
export function useCrmCustomerWorkbenchRuntime() {
  const queryClient = useQueryClient();
  const initialUrlState = useMemo(readCustomerWorkbenchUrlState, []);
  const [draftFilters, setDraftFilters] = useState<CustomerWorkbenchListFilters>(
    initialUrlState.filters,
  );
  const [submittedFilters, setSubmittedFilters] = useState<CustomerWorkbenchListFilters>(
    initialUrlState.filters,
  );
  const [page, setPage] = useState(initialUrlState.page);
  const [viewScope, setViewScope] = useState<CustomerWorkbenchViewScope>(
    initialUrlState.viewScope,
  );
  const [pageSize, setPageSize] = useState(initialUrlState.pageSize);
  const [sortField, setSortField] = useState(initialUrlState.sortField);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">(
    initialUrlState.sortDirection,
  );
  const [selectedCustomerId, setSelectedCustomerId] = useState<number>();
  const [contactDrawerPage, setContactDrawerPage] = useState(1);
  const [selectedDetailCustomerId, setSelectedDetailCustomerId] = useState<number>();
  const [nlQueryParseResult, setNlQueryParseResult] =
    useState<CustomerWorkbenchNlQueryParseResult | null>(null);
  const [nlQueryErrorMessage, setNlQueryErrorMessage] = useState("");

  const listParams = useMemo(
    () => ({
      ...crmCustomerWorkbenchDefaultListParams,
      ...submittedFilters,
      viewScope,
      page,
      pageSize,
      sortField,
      sortDirection,
    }),
    [page, pageSize, sortDirection, sortField, submittedFilters, viewScope],
  );

  useEffect(() => {
    writeCustomerWorkbenchUrlState(listParams);
  }, [listParams]);

  const metadataQuery = useQuery({
    queryKey: [...crmCustomerWorkbenchQueryRootKey, "metadata"],
    queryFn: fetchCustomerWorkbenchMetadata,
  });

  const listQuery = useQuery({
    queryKey: [...crmCustomerWorkbenchQueryRootKey, "customers", listParams],
    queryFn: () => fetchCustomerWorkbenchList(listParams),
  });

  useEffect(() => {
    const total = listQuery.data?.total;
    if (total === undefined || pageSize <= 0) {
      return;
    }
    const maxPage = Math.max(1, Math.ceil(total / pageSize));
    if (page > maxPage) {
      setPage(maxPage);
    }
  }, [listQuery.data?.total, page, pageSize]);

  const contactsQuery = useQuery({
    queryKey: [
      ...crmCustomerWorkbenchQueryRootKey,
      "customer-contacts",
      selectedCustomerId,
      contactDrawerPage,
    ],
    queryFn: () =>
      fetchCustomerWorkbenchContacts({
        customerId: selectedCustomerId as number,
        page: contactDrawerPage,
        pageSize: 20,
      }),
    enabled: Boolean(selectedCustomerId),
  });

  const detailQuery = useQuery({
    queryKey: [...crmCustomerWorkbenchQueryRootKey, "customer-detail", selectedDetailCustomerId],
    queryFn: () => fetchCustomerWorkbenchDetail(selectedDetailCustomerId as number),
    enabled: Boolean(selectedDetailCustomerId),
  });

  const nlQueryParseMutation = useMutation({
    mutationFn: parseCustomerWorkbenchNlQuery,
    onSuccess: setNlQueryParseResult,
  });

  const assignmentMutation = useMutation({
    mutationFn: confirmCustomerAssignment,
    onSuccess: async (result) => {
      Message.success(`客户分配完成，更新 ${result.updatedCount} 条关系`);
      await queryClient.invalidateQueries({ queryKey: crmCustomerWorkbenchQueryRootKey });
    },
    onError: (error) => {
      Message.error(formatCustomerWorkbenchError(error, "客户分配失败，请稍后重试"));
    },
  });

  const replaceSharedOwnerMutation = useMutation({
    mutationFn: replaceCustomerSharedOwners,
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: crmCustomerWorkbenchQueryRootKey });
      Message.success(
        `共享销售已更新：新增 ${result.addedCount} 人，移除 ${result.removedCount} 人`,
      );
    },
    onError: (error) => {
      Message.error(formatCustomerWorkbenchError(error, "更新共享销售失败，请稍后重试"));
    },
  });

  const saveCustomerMutation = useMutation({
    mutationFn: ({ customerId, payload }: { customerId?: number; payload: CustomerWorkbenchFormPayload }) =>
      customerId
        ? updateCustomerWorkbenchCustomer({ customerId, payload })
        : createCustomerWorkbenchCustomer(payload),
    onSuccess: async (item) => {
      if (item.vectorSyncWarning) Message.warning({ content: item.vectorSyncWarning, duration: 8000 });
      else Message.success("客户资料已保存");
      setSelectedDetailCustomerId(undefined);
      await queryClient.invalidateQueries({ queryKey: crmCustomerWorkbenchQueryRootKey });
    },
    onError: (error) => {
      Message.error(formatCustomerWorkbenchError(error, "客户资料保存失败，请稍后重试"));
    },
  });

  const disableCustomerMutation = useMutation({
    mutationFn: disableCustomerWorkbenchCustomer,
    onSuccess: async () => {
      Message.success("客户已停用");
      await queryClient.invalidateQueries({ queryKey: crmCustomerWorkbenchQueryRootKey });
    },
    onError: (error) => {
      Message.error(formatCustomerWorkbenchError(error, "客户停用失败，请稍后重试"));
    },
  });

  const enableCustomerMutation = useMutation({
    mutationFn: enableCustomerWorkbenchCustomer,
    onSuccess: async () => {
      Message.success("客户已启用");
      await queryClient.invalidateQueries({ queryKey: crmCustomerWorkbenchQueryRootKey });
    },
    onError: (error) => {
      Message.error(formatCustomerWorkbenchError(error, "客户启用失败，请稍后重试"));
    },
  });

  const deleteCustomerMutation = useMutation({
    mutationFn: deleteCustomerWorkbenchCustomer,
    onSuccess: async () => {
      Message.success("客户已删除");
      await queryClient.invalidateQueries({ queryKey: crmCustomerWorkbenchQueryRootKey });
    },
    onError: (error) => {
      Message.error(formatCustomerWorkbenchError(error, "客户删除失败，请稍后重试"));
    },
  });

  const listPage = listQuery.data ?? {
    items: [],
    total: 0,
    page,
    pageSize,
  };

  return {
    draftFilters,
    setDraftFilters,
    submittedFilters,
    listParams,
    items: listPage.items,
    total: listPage.total,
    page,
    pageSize,
    viewScope,
    sortField,
    sortDirection,
    metadata: metadataQuery.data,
    staffOptions: metadataQuery.data?.staffOptions ?? [],
    contactsPage: contactsQuery.data,
    selectedCustomerId,
    selectedCustomerDetail: detailQuery.data,
    selectedDetailCustomerId,
    loading: listQuery.isLoading,
    contactsLoading: contactsQuery.isLoading,
    detailLoading: detailQuery.isLoading,
    mutating:
      assignmentMutation.isPending ||
      replaceSharedOwnerMutation.isPending ||
      saveCustomerMutation.isPending ||
      disableCustomerMutation.isPending ||
      enableCustomerMutation.isPending ||
      deleteCustomerMutation.isPending ||
      nlQueryParseMutation.isPending,
    nlQueryParsing: nlQueryParseMutation.isPending,
    nlQueryParseResult,
    nlQueryErrorMessage,
    errorMessage:
      listQuery.error || metadataQuery.error
        ? formatCustomerWorkbenchError(
            listQuery.error ?? metadataQuery.error,
            "客户工作台数据加载失败，请稍后重试。",
          )
        : "",
    handleSearch() {
      setSubmittedFilters(normalizeCustomerWorkbenchFilters(draftFilters));
      setPage(1);
    },
    /** 更新智能查询草稿；输入清空时同步清除识别摘要与错误。 */
    handleNlQueryChange(nlQuery: string) {
      setDraftFilters((current) => ({ ...current, nlQuery }));
      if (!nlQuery.trim()) {
        setNlQueryParseResult(null);
        setNlQueryErrorMessage("");
      }
    },
    async handleSmartSearch() {
      const normalizedFilters = normalizeCustomerWorkbenchFilters(draftFilters);
      setNlQueryErrorMessage("");
      if (!normalizedFilters.nlQuery) {
        setNlQueryParseResult(null);
        setSubmittedFilters(normalizedFilters);
        setPage(1);
        return;
      }
      let parsed: CustomerWorkbenchNlQueryParseResult;
      try {
        parsed = await nlQueryParseMutation.mutateAsync(normalizedFilters.nlQuery);
      } catch (error) {
        setNlQueryParseResult(null);
        setNlQueryErrorMessage(
          formatCustomerWorkbenchError(error, "智能查询解析失败，请改用普通筛选。"),
        );
        return;
      }
      if (!parsed.executable) {
        return;
      }
      setSubmittedFilters(normalizedFilters);
      setPage(1);
    },
    handleReset() {
      const defaults = { isDisabled: false };
      setDraftFilters(defaults);
      setSubmittedFilters(defaults);
      setNlQueryParseResult(null);
      setNlQueryErrorMessage("");
      setPage(1);
    },
    handlePageChange(nextPage: number) {
      setPage(Math.max(nextPage, 1));
    },
    handleViewScopeChange(nextViewScope: CustomerWorkbenchViewScope) {
      setViewScope(nextViewScope);
      setPage(1);
    },
    handlePageSizeChange(nextPageSize: number) {
      setPageSize(nextPageSize);
      setPage(1);
    },
    handleSort(nextSortField: string, nextSortDirection: "asc" | "desc") {
      setSortField(nextSortField);
      setSortDirection(nextSortDirection);
      setPage(1);
    },
    handleOpenContacts(customerId: number) {
      setSelectedCustomerId(customerId);
      setContactDrawerPage(1);
    },
    handleCloseContacts() {
      setSelectedCustomerId(undefined);
      setContactDrawerPage(1);
    },
    handleContactPageChange(nextPage: number) {
      setContactDrawerPage(nextPage);
    },
    handleOpenCustomer(customerId?: number) {
      setSelectedDetailCustomerId(customerId ?? 0);
    },
    handleCloseCustomer() {
      setSelectedDetailCustomerId(undefined);
    },
    handleResolveSelectedCustomerIds(selection: CustomerWorkbenchSelectionResolvePayload) {
      if (selection.mode === "selected_rows") {
        return Promise.resolve(dedupeCustomerIds(selection.customerIds));
      }
      return fetchAllCustomerIdsForCurrentQuery(listParams);
    },
    async handleConfirmAssignment(payload: CustomerAssignmentConfirmPayload) {
      await assignmentMutation.mutateAsync(payload);
    },
    async handleReplaceCustomerSharedOwners(
      customerId: number,
      currentUserIds: number[],
      targetUserIds: number[],
    ) {
      await replaceSharedOwnerMutation.mutateAsync({
        customerId,
        currentUserIds,
        targetUserIds,
        currentUserId: 1,
      });
    },
    async handleSaveCustomer(customerId: number | undefined, payload: CustomerWorkbenchFormPayload) {
      await saveCustomerMutation.mutateAsync({ customerId, payload });
    },
    async handleDisableCustomer(customerId: number) {
      await disableCustomerMutation.mutateAsync(customerId);
    },
    async handleEnableCustomer(customerId: number) {
      await enableCustomerMutation.mutateAsync(customerId);
    },
    async handleDeleteCustomer(customerId: number) {
      await deleteCustomerMutation.mutateAsync(customerId);
    },
    searchOrganizations(keyword: string) {
      return searchCustomerWorkbenchOrganizations({
        keyword,
        limit: 20,
      });
    },
    searchOrganizationUnits(orgId: number, keyword: string) {
      return searchCustomerWorkbenchOrganizationUnits({
        orgId,
        keyword,
        limit: 50,
      });
    },
  };
}

/** 按当前筛选条件分页拉取全部客户 ID。 */
async function fetchAllCustomerIdsForCurrentQuery(
  listParams: CustomerWorkbenchListParams,
): Promise<number[]> {
  const customerIds: number[] = [];
  let nextPage = 1;
  let total = Number.POSITIVE_INFINITY;
  while (customerIds.length < total) {
    const pageResult = await fetchCustomerWorkbenchList({
      ...listParams,
      page: nextPage,
      pageSize: CUSTOMER_WORKBENCH_ALL_SELECTION_PAGE_SIZE,
    });
    total = pageResult.total;
    customerIds.push(...pageResult.items.map((item) => item.id));
    if (pageResult.items.length === 0) {
      break;
    }
    nextPage += 1;
  }
  return dedupeCustomerIds(customerIds);
}

/** 按目标集合更新单个客户的共享销售，新增和撤销分别落库。 */
async function replaceCustomerSharedOwners({
  customerId,
  currentUserIds,
  targetUserIds,
  currentUserId,
}: CustomerSharedOwnerReplacePayload): Promise<{ addedCount: number; removedCount: number }> {
  const currentSet = new Set(dedupeUserIds(currentUserIds));
  const targetSet = new Set(dedupeUserIds(targetUserIds));
  const addUserIds = Array.from(targetSet).filter((userId) => !currentSet.has(userId));
  const removeUserIds = Array.from(currentSet).filter((userId) => !targetSet.has(userId));
  let addedCount = 0;
  let removedCount = 0;
  for (const targetUserId of addUserIds) {
    const result = await confirmCustomerAssignment({
      customerIds: [customerId],
      targetUserId,
      relationType: "shared_owner",
      conflictStrategy: "add_shared",
      currentUserId,
      permissionLevel: "follow",
      sourceType: "manual",
      reason: "客户工作台添加共享销售",
    });
    addedCount += result.updatedCount;
  }
  for (const targetUserId of removeUserIds) {
    const result = await revokeCustomerSharedOwner({
      customerIds: [customerId],
      targetUserId,
      currentUserId,
      reason: "客户工作台移除共享销售",
    });
    removedCount += result.updatedCount;
  }
  return { addedCount, removedCount };
}

/** 去重并清洗客户 ID。 */
function dedupeCustomerIds(customerIds: number[]) {
  return Array.from(new Set(customerIds.filter((customerId) => Number.isFinite(customerId) && customerId > 0)));
}

/** 去重并清洗员工用户 ID。 */
function dedupeUserIds(userIds: number[]) {
  return Array.from(new Set(userIds.filter((userId) => Number.isFinite(userId))));
}

/** 从当前 URL 读取客户工作台可分享的查询状态。 */
function readCustomerWorkbenchUrlState(): CustomerWorkbenchUrlState {
  const defaultSortField = crmCustomerWorkbenchDefaultListParams.sortField ?? "contact_count";
  const defaultSortDirection = crmCustomerWorkbenchDefaultListParams.sortDirection ?? "desc";
  if (typeof window === "undefined") {
    return {
      filters: { isDisabled: false },
      viewScope: "mine",
      page: crmCustomerWorkbenchDefaultListParams.page,
      pageSize: crmCustomerWorkbenchDefaultListParams.pageSize,
      sortField: defaultSortField,
      sortDirection: defaultSortDirection,
    };
  }

  const params = new URLSearchParams(window.location.search);
  const status = params.get("status");
  const page = parsePositiveInteger(params.get("page"));
  const pageSize = parsePositiveInteger(params.get("pageSize"));
  const sortField = params.get("sort");
  const sortDirection = params.get("direction");
  const salesUserId = parsePositiveInteger(params.get("salesUserId"));
  const resolvedSortDirection =
    sortDirection === "asc" || sortDirection === "desc"
      ? sortDirection
      : defaultSortDirection;
  const hasNewSortDefaultMarker =
    params.get("sortDefault") === CUSTOMER_WORKBENCH_SORT_DEFAULT_VERSION;
  let resolvedSortField =
    sortField && CUSTOMER_WORKBENCH_SORT_FIELDS.has(sortField) ? sortField : defaultSortField;
  // 旧会话/标签常残留 sort=created_at（上一版产品默认）。未见新默认标记时迁移到联系人数降序，
  // 避免「联系人」降序按钮一直灰色；写入 URL 后带上标记，之后手动选创建时间会保留。
  if (
    !hasNewSortDefaultMarker &&
    (!sortField ||
      (resolvedSortField === LEGACY_DEFAULT_SORT_FIELD && resolvedSortDirection === "desc"))
  ) {
    resolvedSortField = defaultSortField;
  }

  return {
    filters: normalizeCustomerWorkbenchFilters({
      regionSchoolText: readOptionalUrlText(params, "regionSchoolText"),
      customerName: readOptionalUrlText(params, "customerName"),
      salesUserId,
      salesUserName: readOptionalUrlText(params, "salesUserName"),
      isDisabled: status === "all" ? undefined : status === "disabled" ? true : false,
      nlQuery: readOptionalUrlText(params, "nlQuery"),
    }),
    viewScope: params.get("viewScope") === "all" ? "all" : "mine",
    page: page ?? crmCustomerWorkbenchDefaultListParams.page,
    pageSize:
      pageSize && CUSTOMER_WORKBENCH_PAGE_SIZE_OPTIONS.has(pageSize)
        ? pageSize
        : crmCustomerWorkbenchDefaultListParams.pageSize,
    sortField: resolvedSortField,
    sortDirection: resolvedSortDirection,
  };
}

/** 把客户工作台查询状态写入 URL，同时保留其他业务参数。 */
function writeCustomerWorkbenchUrlState(params: CustomerWorkbenchListParams) {
  if (typeof window === "undefined") {
    return;
  }
  const url = new URL(window.location.href);
  setUrlText(url.searchParams, "viewScope", params.viewScope ?? "mine");
  setUrlText(url.searchParams, "page", String(params.page));
  setUrlText(url.searchParams, "pageSize", String(params.pageSize));
  setUrlText(url.searchParams, "regionSchoolText", params.regionSchoolText);
  setUrlText(url.searchParams, "customerName", params.customerName);
  setUrlText(url.searchParams, "salesUserId", params.salesUserId?.toString());
  setUrlText(url.searchParams, "salesUserName", params.salesUserName);
  setUrlText(url.searchParams, "nlQuery", params.nlQuery);
  setUrlText(
    url.searchParams,
    "status",
    params.isDisabled === undefined ? "all" : params.isDisabled ? "disabled" : "active",
  );
  setUrlText(url.searchParams, "sort", params.sortField);
  setUrlText(url.searchParams, "direction", params.sortDirection);
  setUrlText(url.searchParams, "sortDefault", CUSTOMER_WORKBENCH_SORT_DEFAULT_VERSION);
  window.history.replaceState(window.history.state, "", url);
}

/** 解析正整数 URL 参数。 */
function parsePositiveInteger(value: string | null) {
  if (!value || !/^\d+$/.test(value)) return undefined;
  const parsed = Number(value);
  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : undefined;
}

/** 读取并清洗可选 URL 文本。 */
function readOptionalUrlText(params: URLSearchParams, key: string) {
  const value = params.get(key)?.trim();
  return value || undefined;
}

/** 设置或移除单个 URL 文本参数。 */
function setUrlText(params: URLSearchParams, key: string, value?: string) {
  if (value) {
    params.set(key, value);
    return;
  }
  params.delete(key);
}
